import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Card } from "./ui/card";
import { Activity } from "lucide-react";

/**
 * GitHub / Skool-style daily activity heatmap.
 * 53 columns × 7 rows (Sun→Sat). Reads from GET /api/profile/activity.
 *
 * Activity sources tallied server-side (UTC-bucketed):
 *   • shots logged            • weight entries       • cycles created
 *   • community topics        • community replies    • playbook lessons completed
 */
const CELL = 12;    // px cell size
const GAP = 3;      // px between cells
const LEFT_PAD = 34; // space for day labels
const TOP_PAD = 22;  // space for month labels

// Intensity scale — 5 stops from empty → max brand
function bucketColor(count) {
  if (count <= 0) return "var(--surface-2)";
  if (count === 1) return "color-mix(in srgb, var(--brand) 22%, var(--surface-2))";
  if (count <= 3) return "color-mix(in srgb, var(--brand) 45%, var(--surface-2))";
  if (count <= 6) return "color-mix(in srgb, var(--brand) 70%, var(--surface-2))";
  return "var(--brand)";
}
const MONTH_LABELS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const DAY_ABBR = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const SHOW_DAYS = new Set([1, 3, 5]); // Mon / Wed / Fri (like Skool)

export default function ActivityHeatmap({ compact = false }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/profile/activity");
        if (mounted) setData(data);
      } catch (err) {
        if (mounted) setError(err?.message || "Failed to load activity");
      }
    })();
    return () => { mounted = false; };
  }, []);

  const grid = useMemo(() => {
    if (!data?.days?.length) return null;
    // Grid start: nearest previous Sunday from data.range_start
    const first = new Date(data.days[0].date + "T00:00:00Z");
    const firstDow = first.getUTCDay(); // 0=Sun
    // Trim to last 53 weeks ending on the last data day
    const cells = data.days.map((d) => ({ ...d, dt: new Date(d.date + "T00:00:00Z") }));
    // Prepend blank cells so first column starts at Sunday
    const leading = Array.from({ length: firstDow }, () => ({ blank: true }));
    const all = [...leading, ...cells];
    // Round up total cells to full weeks
    while (all.length % 7 !== 0) all.push({ blank: true });
    // Chunk into weeks (columns)
    const cols = [];
    for (let i = 0; i < all.length; i += 7) cols.push(all.slice(i, i + 7));
    return cols;
  }, [data]);

  const width = grid ? LEFT_PAD + grid.length * (CELL + GAP) + 6 : 900;
  const height = TOP_PAD + 7 * (CELL + GAP) + 4;

  const monthTicks = useMemo(() => {
    if (!grid) return [];
    const ticks = [];
    let lastMonth = -1;
    grid.forEach((col, ci) => {
      const first = col.find((c) => c.dt);
      if (!first) return;
      const m = first.dt.getUTCMonth();
      if (m !== lastMonth) {
        ticks.push({ x: LEFT_PAD + ci * (CELL + GAP), label: MONTH_LABELS[m] });
        lastMonth = m;
      }
    });
    return ticks;
  }, [grid]);

  if (compact) {
    return (
      <div className="relative w-full h-full overflow-hidden" data-testid="activity-heatmap-compact">
        {error && <div className="absolute inset-0 flex items-center justify-center text-sm text-[color:var(--danger)] font-mono-num">{error}</div>}
        {grid && (
          <div className="absolute inset-0 flex items-center overflow-x-auto overflow-y-hidden pl-32 sm:pl-40 pr-4">
            <svg width={width} height={height} className="block shrink-0">
              {monthTicks.map((t) => (
                <text key={`m-c-${t.x}`} x={t.x} y={12} className="font-mono-num" fill="var(--text-muted)" fontSize="10" letterSpacing="1">{t.label}</text>
              ))}
              {DAY_ABBR.map((d, i) => (
                SHOW_DAYS.has(i) ? (
                  <text key={d} x={0} y={TOP_PAD + i * (CELL + GAP) + CELL - 2} className="font-mono-num" fill="var(--text-muted)" fontSize="10">{d}</text>
                ) : null
              ))}
              {grid.map((col, ci) => col.map((cell, ri) => {
                if (cell.blank) return null;
                const x = LEFT_PAD + ci * (CELL + GAP);
                const y = TOP_PAD + ri * (CELL + GAP);
                return (
                  <rect
                    key={`${ci}-${ri}`}
                    x={x} y={y} width={CELL} height={CELL}
                    fill={bucketColor(cell.count)} stroke="var(--bg)" strokeWidth="0.5" rx="1.5"
                    onMouseEnter={() => setHover({ x: x + CELL + 6, y: y - 4, date: cell.date, count: cell.count })}
                    onMouseLeave={() => setHover(null)}
                    data-testid={`heatmap-cell-${cell.date}`}
                    style={{ cursor: "pointer" }}
                  />
                );
              }))}
            </svg>
          </div>
        )}
        {data && (
          <div className="absolute top-3 right-3 text-[10px] font-mono-num uppercase tracking-widest text-[color:var(--text-muted)] bg-black/40 backdrop-blur-sm px-2 py-1 pointer-events-none">
            {data.total} events · past 12 mo
          </div>
        )}
        {hover && (
          <div
            className="pointer-events-none absolute z-10 px-2.5 py-1.5 bg-black border border-[color:var(--brand)] font-mono-num text-[11px] uppercase tracking-widest whitespace-nowrap"
            style={{ left: hover.x + 128, top: hover.y + 4, color: "var(--brand)" }}
            data-testid="heatmap-tooltip"
          >
            <div className="text-[color:var(--text)]">{formatDate(hover.date)}</div>
            <div className="text-[color:var(--text-muted)]">
              {hover.count === 0 ? "No activity" : `${hover.count} ${hover.count === 1 ? "event" : "events"}`}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <Card className="bg-[color:var(--surface)] border border-[color:var(--border)] rounded-none p-5" data-testid="activity-heatmap">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="w-8 h-8 flex items-center justify-center" style={{ background: "var(--brand)" }}>
          <Activity className="w-4 h-4" style={{ color: "var(--brand-fg)" }} />
        </div>
        <div>
          <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-[color:var(--text-muted)]">// Activity</div>
          <div className="font-display font-black uppercase tracking-tight text-xl">Daily Activity</div>
        </div>
        <div className="ml-auto text-[11px] font-mono-num uppercase tracking-widest text-[color:var(--text-muted)]">
          {data ? <>{data.total} events · past 12 months (UTC)</> : "loading…"}
        </div>
      </div>

      {error && <div className="text-sm text-[color:var(--danger)] font-mono-num">{error}</div>}

      {grid && (
        <div className="relative">
          <div className="overflow-x-auto pb-2">
            <svg width={width} height={height} className="block">
              {/* month labels */}
              {monthTicks.map((t) => (
                <text key={`m-${t.x}`} x={t.x} y={12} className="font-mono-num" fill="var(--text-muted)" fontSize="10" letterSpacing="1">
                  {t.label}
                </text>
              ))}
              {/* day labels */}
              {DAY_ABBR.map((d, i) => (
                SHOW_DAYS.has(i) ? (
                  <text
                    key={d}
                    x={0}
                    y={TOP_PAD + i * (CELL + GAP) + CELL - 2}
                    className="font-mono-num"
                    fill="var(--text-muted)"
                    fontSize="10"
                  >{d}</text>
                ) : null
              ))}
              {/* cells */}
              {grid.map((col, ci) => col.map((cell, ri) => {
                if (cell.blank) return null;
                const x = LEFT_PAD + ci * (CELL + GAP);
                const y = TOP_PAD + ri * (CELL + GAP);
                return (
                  <rect
                    key={`${ci}-${ri}`}
                    x={x}
                    y={y}
                    width={CELL}
                    height={CELL}
                    fill={bucketColor(cell.count)}
                    stroke="var(--bg)"
                    strokeWidth="0.5"
                    rx="1.5"
                    onMouseEnter={() => setHover({
                      x: x + CELL + 6,
                      y: y - 4,
                      date: cell.date,
                      count: cell.count,
                    })}
                    onMouseLeave={() => setHover(null)}
                    data-testid={`heatmap-cell-${cell.date}`}
                    style={{ cursor: "pointer" }}
                  />
                );
              }))}
            </svg>
          </div>

          {/* Legend */}
          <div className="mt-2 flex items-center justify-end flex-wrap gap-3 text-[11px] font-mono-num uppercase tracking-widest text-[color:var(--text-muted)]">
            <div className="inline-flex items-center gap-1.5">
              <span>Less</span>
              {[0, 1, 3, 6, 10].map((n) => (
                <span key={`legend-${n}`} className="inline-block" style={{ width: CELL, height: CELL, background: bucketColor(n) }} />
              ))}
              <span>More</span>
            </div>
          </div>

          {/* Tooltip */}
          {hover && (
            <div
              className="pointer-events-none absolute z-10 px-2.5 py-1.5 bg-black border border-[color:var(--brand)] font-mono-num text-[11px] uppercase tracking-widest whitespace-nowrap"
              style={{ left: hover.x, top: hover.y, color: "var(--brand)" }}
              data-testid="heatmap-tooltip"
            >
              <div className="text-[color:var(--text)]">{formatDate(hover.date)}</div>
              <div className="text-[color:var(--text-muted)]">
                {hover.count === 0 ? "No activity" : `${hover.count} ${hover.count === 1 ? "event" : "events"}`}
              </div>
            </div>
          )}

          <p className="mt-4 text-[11px] text-[color:var(--text-muted)] leading-relaxed max-w-2xl">
            Tallied from shots, weight logs, cycles, community posts, replies, and completed playbook lessons.
            Timestamps are Coordinated Universal Time (UTC), not your local time zone.
          </p>
        </div>
      )}
    </Card>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso + "T00:00:00Z");
    return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
  } catch { return iso; }
}
